import { appConfig } from '../config/appConfig';
import type { Asset, AssetWithAIExtractions, KnowledgeParticle, PaginationResponse } from '../types/domain';
import type { AssetQualityControlMetadata } from '../utils/assetQualityControl';
import {
  isQualityControlResultReady,
  pollAssetQualityControl,
  resolveAssetQualityControlPayload,
  shouldAutoRunQualityControl,
} from '../utils/assetQualityControl';
import { apiClient } from './apiClient';

/** 短时缓存签名 URL，避免预览与裁切弹窗重复打 /api/oss/get-signed-url */
const signedOssUrlCache = new Map<string, { url: string; expiresAt: number }>();

function signedOssCacheKey(ossId: string, process = '', expiresSeconds = 0): string {
  return `${ossId}\0${process}\0${expiresSeconds}`;
}

/** 同步读缓存（供裁切弹窗与预览共用同一签名直链，命中浏览器图片缓存） */
export function peekSignedOssUrlCache(
  ossId: string,
  options?: { process?: string; expiresSeconds?: number },
): string | null {
  const key = String(ossId || '').trim();
  if (!key) return null;
  const process = String(options?.process || '').trim();
  const expiresSeconds = options?.expiresSeconds && options.expiresSeconds > 0
    ? Math.floor(options.expiresSeconds)
    : 0;
  const cached = signedOssUrlCache.get(signedOssCacheKey(key, process, expiresSeconds));
  if (!cached || cached.expiresAt <= Date.now() + 15_000) return null;
  return cached.url;
}

export function inferAssetType(fileName: string, mimeType?: string | null): string {
  const loweredName = fileName.toLowerCase();
  const loweredType = (mimeType || '').toLowerCase();
  if (loweredType.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|bmp)$/.test(loweredName)) return 'image';
  if (loweredType.startsWith('audio/')) return 'audio';
  if (loweredType.startsWith('video/')) return 'video';
  if (loweredType.includes('pdf')) return 'pdf';
  return 'document';
}

export const assetService = {
  async listAssets(input: {
    userId: string;
    page?: number;
    pageSize?: number;
    search?: string;
    sortOrder?: 'asc' | 'desc';
    subject?: string;
    /** `pas` = only practice answer sheets; `exclude_pas` = ordinary assets. */
    typeFilter?: 'pas' | 'exclude_pas';
    dayKey?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginationResponse<Asset>> {
    const params = new URLSearchParams({
      userId: input.userId,
      page: String(input.page ?? 1),
      pageSize: String(input.pageSize ?? 20),
      search: input.search ?? '',
      sortOrder: input.sortOrder ?? 'desc',
    });
    if (input.subject) params.set('subject', input.subject);
    if (input.typeFilter === 'pas' || input.typeFilter === 'exclude_pas') {
      params.set('type', input.typeFilter);
    }
    if (input.dayKey) params.set('dayKey', input.dayKey);
    if (input.startDate) params.set('startDate', input.startDate);
    if (input.endDate) params.set('endDate', input.endDate);
    return apiClient.getEnvelope<PaginationResponse<Asset>>(`/api/assets?${params}`);
  },

  async listAssetDayIndex(input: {
    userId: string;
    subject?: string;
    typeFilter?: 'pas' | 'exclude_pas';
  }): Promise<{ days: Array<{ dayKey: string; count: number }> }> {
    const params = new URLSearchParams({ userId: input.userId });
    if (input.subject) params.set('subject', input.subject);
    if (input.typeFilter === 'pas' || input.typeFilter === 'exclude_pas') {
      params.set('type', input.typeFilter);
    }
    const payload = await apiClient.getEnvelope<{ days?: Array<{ dayKey?: string; count?: number }> }>(
      `/api/assets/day-index?${params}`,
    );
    return {
      days: Array.isArray(payload?.days)
        ? payload.days
          .map((entry) => ({
            dayKey: String(entry?.dayKey || '').trim(),
            count: Math.max(0, Number(entry?.count) || 0),
          }))
          .filter((entry) => entry.dayKey)
        : [],
    };
  },

  async listAssetsByDay(input: {
    userId: string;
    dayKey: string;
    subject?: string;
    typeFilter?: 'pas' | 'exclude_pas';
    pageSize?: number;
  }): Promise<Asset[]> {
    const result = await assetService.listAssets({
      userId: input.userId,
      dayKey: input.dayKey,
      subject: input.subject,
      typeFilter: input.typeFilter,
      page: 1,
      pageSize: input.pageSize ?? 50,
      sortOrder: 'desc',
    });
    return Array.isArray(result.data) ? result.data : [];
  },

  async listKnowledgeParticleCounts(assetIds: string[]): Promise<{
    counts: Record<string, number>;
    previews: Record<string, string>;
  }> {
    const normalizedIds = Array.from(new Set(assetIds.map((id) => String(id || '').trim()).filter(Boolean)));
    if (normalizedIds.length === 0) {
      return { counts: {}, previews: {} };
    }
    const payload = await apiClient.getEnvelope<Array<{
      assetId?: string;
      particleCount?: number;
      particlePreview?: string;
    }>>(
      `/api/assets/knowledge-particle-counts?assetIds=${encodeURIComponent(normalizedIds.join(','))}`,
    );
    const counts: Record<string, number> = {};
    const previews: Record<string, string> = {};
    for (const row of Array.isArray(payload) ? payload : []) {
      const assetId = String(row?.assetId || '').trim();
      if (!assetId) continue;
      counts[assetId] = Math.max(0, Number(row?.particleCount) || 0);
      const preview = String(row?.particlePreview || '').trim();
      if (preview) {
        previews[assetId] = preview;
      }
    }
    return { counts, previews };
  },

  async getAsset(assetId: string): Promise<Asset> {
    try {
      return await apiClient.getEnvelope<AssetWithAIExtractions>(`/api/assets/${assetId}/with-ai-extractions`);
    } catch {
      return apiClient.getEnvelope<Asset>(`/api/assets/${assetId}`);
    }
  },

  async getAssetWithAIExtractions(assetId: string): Promise<AssetWithAIExtractions> {
    return apiClient.getEnvelope<AssetWithAIExtractions>(`/api/assets/${assetId}/with-ai-extractions`);
  },

  async getUnextractedAssets(userId: string, limit = 100) {
    const params = new URLSearchParams({ userId, limit: String(limit) });
    return apiClient.getEnvelope<{ data: Asset[]; total: number }>(`/api/assets/unextracted?${params}`);
  },

  async listKnowledgeParticlesByAsset(assetId: string): Promise<KnowledgeParticle[]> {
    return apiClient.getEnvelope<KnowledgeParticle[]>(`/api/assets/${assetId}/knowledge-particles`);
  },

  async uploadImageAsset(input: {
    userId: string;
    file: File;
    description?: string;
    subject?: string;
    /**
     * Upload purpose harness: learning_diary | pas | kp_embed | factory_figure | geometry_candidate.
     * Preferred over guessing via subject/description.
     */
    purpose?: string;
    /** @deprecated Prefer `purpose`. Persist into asset.metadata.source when purpose omitted. */
    assetSource?: string;
    /** @deprecated Prefer `purpose`. When false, treated as non-diary (kp_embed) if purpose omitted. */
    homeDiaryVisible?: boolean;
    type?: 'photo' | 'pas' | 'ttqas';
    pasAnchor?: {
      kpId: string;
      planId?: string;
      sessionId?: string;
      batch?: string;
      format?: string;
      raw?: string;
    };
    ttqasAnchor?: {
      kpId: string;
      ttqaId: string;
      sessionId?: string;
      batch?: string;
      format?: string;
      raw?: string;
    };
  }): Promise<Asset> {
    const formData = new FormData();
    formData.append('file', input.file, input.file.name);
    formData.append('userId', input.userId);
    formData.append('platformInfo', JSON.stringify({ source: 'webapp2' }));
    if (input.description) formData.append('description', input.description);
    if (input.subject) formData.append('subject', input.subject);
    if (input.purpose) formData.append('purpose', input.purpose);
    if (input.assetSource) formData.append('assetSource', input.assetSource);
    if (input.homeDiaryVisible === false) formData.append('homeDiaryVisible', 'false');
    if (input.type) {
      formData.append('assetType', input.type);
      formData.append('type', input.type);
    }
    if (input.pasAnchor?.kpId) {
      formData.append('pasAnchor', JSON.stringify(input.pasAnchor));
    }
    if (input.ttqasAnchor?.kpId && input.ttqasAnchor?.ttqaId) {
      formData.append('ttqasAnchor', JSON.stringify(input.ttqasAnchor));
    }

    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: Asset;
      asset?: Asset;
      error?: string;
      code?: string;
    }>(
      '/api/oss/upload',
      { method: 'POST', body: formData, auth: true },
    ).catch((error: unknown) => {
      const record = error as Error & {
        payload?: { code?: string; error?: string };
      };
      const code = typeof record.payload?.code === 'string' ? record.payload.code : '';
      if (code === 'PAS_PRINT_SESSION_DUPLICATE' || code === 'TTQAS_PRINT_SESSION_DUPLICATE') {
        const uploadError = new Error(record.payload?.error || record.message || '该答题纸已扫描过') as Error & { code?: string };
        uploadError.code = code;
        throw uploadError;
      }
      throw error;
    });
    const asset = payload.data || payload.asset;
    if (!payload.success || !asset) {
      const uploadError = new Error(payload.error || '上传失败') as Error & { code?: string };
      if (payload.code) uploadError.code = payload.code;
      throw uploadError;
    }
    return asset;
  },

  async getExtractionJob(taskId: string) {
    return apiClient.getEnvelope<{
      taskId: string;
      assetId: string;
      userId: string | null;
      status: 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
      result: Record<string, unknown> | null;
      error: string | null;
      createdAt: string;
      updatedAt: string;
      startedAt: string | null;
      finishedAt: string | null;
    }>(`/api/knowledge-particle-extraction/jobs/${encodeURIComponent(taskId)}`);
  },

  async triggerKnowledgeExtraction(input: {
    userId?: string;
    assetId: string;
    ossId?: string;
    confirmPasSuspectedExtraction?: boolean;
  }) {
    try {
      const payload = await apiClient.requestJson<{
        success?: boolean;
        data?: unknown;
        message?: string;
        error?: string;
        code?: string;
      }>(
        '/api/knowledge-particle-extraction/extract-knowledge-particles',
        {
          method: 'POST',
          body: {
            userId: input.userId,
            assetId: input.assetId,
            ossid: input.ossId,
            extractionTarget: 'asset',
            ...(input.confirmPasSuspectedExtraction ? { confirmPasSuspectedExtraction: true } : {}),
          },
          auth: true,
          timeoutMs: appConfig.particleExtractionSubmitTimeoutMs,
        },
      );
      if (payload?.success === false) {
        const code = String(payload.code || '').trim();
        const message = payload.error || payload.message || '提取失败';
        if (code === 'MATERIAL_EXTRACTING' || /正在提取/.test(message)) {
          return {
            accepted: true,
            assetId: input.assetId,
            status: 'processing' as const,
            message,
          };
        }
        throw new Error(message);
      }

      const data = payload?.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const record = data as Record<string, unknown>;
        if (record.status === 'skipped_pas_suspected' || payload.code === 'PAS_ANSWER_SHEET_SUSPECTED') {
          return {
            accepted: false,
            skippedPasSuspected: true,
            assetId: record.assetId || input.assetId,
            status: 'skipped_pas_suspected' as const,
            message: payload.message
              || '检测到实战答题纸特征，已跳过自动提取。请通过实战答题纸通道上传。',
          };
        }
        if (
          (record.status === 'processing' || record.status === 'queued')
          && typeof record.taskId === 'string'
        ) {
          return {
            accepted: true,
            taskId: record.taskId,
            assetId: record.assetId || input.assetId,
            status: record.status === 'queued' ? 'queued' as const : 'processing' as const,
            message: payload.message || '知识颗粒提取已提交后台处理',
          };
        }
      }

      return payload;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error || '');
      if (/MATERIAL_EXTRACTING|正在提取/.test(message)) {
        return {
          accepted: true,
          assetId: input.assetId,
          status: 'processing' as const,
          message,
        };
      }
      throw error;
    }
  },

  async getSignedOssUrl(
    ossId: string,
    options?: { process?: string; expiresSeconds?: number },
  ): Promise<string> {
    const key = String(ossId || '').trim();
    if (!key) return '';
    const process = String(options?.process || '').trim();
    const expiresSeconds = options?.expiresSeconds && options.expiresSeconds > 0
      ? Math.floor(options.expiresSeconds)
      : 0;
    const peeked = peekSignedOssUrlCache(key, { process, expiresSeconds: expiresSeconds || undefined });
    if (peeked) return peeked;

    const params = new URLSearchParams({ ossId: key });
    if (process) params.set('process', process);
    if (expiresSeconds > 0) params.set('expiresSeconds', String(expiresSeconds));
    const url = await apiClient.getEnvelope<string>(`/api/oss/get-signed-url?${params}`);
    const trimmed = String(url || '').trim();
    if (trimmed) {
      const ttlMs = expiresSeconds > 0
        ? Math.max(30_000, expiresSeconds * 1000 - 60_000)
        : 10 * 60 * 1000;
      signedOssUrlCache.set(
        signedOssCacheKey(key, process, expiresSeconds),
        { url: trimmed, expiresAt: Date.now() + ttlMs },
      );
    }
    return trimmed;
  },

  async deleteAssetCascade(input: { userId: string; assetId: string }) {
    const payload = await apiClient.requestJson<{ success?: boolean; data?: boolean; error?: string }>(
      `/api/assets/${input.assetId}/delete`,
      { method: 'POST', body: { userId: input.userId }, auth: true },
    );
    if (!payload.success || !payload.data) throw new Error(payload.error || '删除失败');
  },

  /** Persist PAS canvas regions (QG / deliberate-practice nodes) on asset.metadata.pasWorkbench */
  async savePasWorkbench(input: {
    assetId: string;
    anchorPracticeItems?: Array<{
      id: string;
      question: string;
      answer: string;
      notes?: string;
      createdAt: string;
      studyCount: number;
      status: string;
      expiresAt: string;
      maxStudyCount: number;
      negativeExample?: boolean;
      proximityLevel?: number;
    }>;
    regions: Array<{
      id: string;
      rectNormalized: { x: number; y: number; width: number; height: number };
      gateStatus: string;
      gateTheme?: string;
      practiceDraftId?: string;
      practiceItems?: Array<{
        id: string;
        question: string;
        answer: string;
        notes?: string;
        createdAt: string;
        studyCount: number;
        status: string;
        expiresAt: string;
        maxStudyCount: number;
        negativeExample?: boolean;
      }>;
    }>;
  }): Promise<Asset> {
    const payload = await apiClient.requestJson<{ success?: boolean; data?: Asset; error?: string }>(
      `/api/assets/${encodeURIComponent(input.assetId)}/pas-workbench`,
      {
        method: 'PUT',
        body: {
          version: 1,
          regions: input.regions,
          ...(Array.isArray(input.anchorPracticeItems)
            ? { anchorPracticeItems: input.anchorPracticeItems }
            : {}),
        },
        auth: true,
      },
    );
    if (!payload.success || !payload.data) {
      throw new Error(payload.error || '保存实战标注失败');
    }
    return payload.data;
  },

  async listPasAssetsByParticleId(particleId: string): Promise<Asset[]> {
    const payload = await apiClient.getEnvelope<Asset[]>(
      `/api/assets/pas-by-particle/${encodeURIComponent(particleId)}`,
    );
    return Array.isArray(payload) ? payload : [];
  },

  async getAssetQualityControl(assetId: string) {
    return apiClient.getEnvelope<{
      asset_id: string;
      quality_control: Record<string, unknown> | null;
      latest_records: unknown[];
    }>(`/api/assets/${encodeURIComponent(assetId)}/quality-control`);
  },

  async rerunAssetQualityControl(assetId: string): Promise<{
    data: {
      asset_id: string;
      quality_control: Record<string, unknown> | null;
      latest_records: unknown[];
      qa_passed?: boolean;
      qa_status?: string;
    } | null;
    error?: string;
  }> {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: {
        asset_id: string;
        quality_control: Record<string, unknown> | null;
        latest_records: unknown[];
        qa_passed?: boolean;
        qa_status?: string;
      };
      error?: string;
    }>(`/api/assets/${encodeURIComponent(assetId)}/quality-control/re-run`, {
      method: 'POST',
      body: {},
      auth: true,
      timeoutMs: appConfig.qualityControlRunTimeoutMs,
    });

    if (payload?.success === false && !payload?.data) {
      throw new Error(payload.error || '重新品控失败');
    }

    return {
      data: payload?.data ?? null,
      error: payload?.success === false ? (payload.error || '重新品控失败') : undefined,
    };
  },

  async ensureAssetQualityControl(
    assetId: string,
    current?: AssetQualityControlMetadata | null,
    options?: { particleCount?: number },
  ): Promise<{
    qualityControl: AssetQualityControlMetadata | null;
    rerunAttempted: boolean;
    rerunError?: string;
  }> {
    const baselineCheckedAt = current?.last_checked_at || null;
    const needsRun = shouldAutoRunQualityControl(current, options);

    if (!needsRun) {
      const data = await this.getAssetQualityControl(assetId);
      return {
        qualityControl: resolveAssetQualityControlPayload(data) || current || null,
        rerunAttempted: false,
      };
    }

    let rerunError: string | undefined;
    try {
      const rerunResponse = await this.rerunAssetQualityControl(assetId);
      rerunError = rerunResponse.error;
      const rerunQualityControl = rerunResponse.data
        ? resolveAssetQualityControlPayload(rerunResponse.data)
        : null;
      if (isQualityControlResultReady(rerunQualityControl, {
        baselineCheckedAt,
        untilNotStale: true,
      })) {
        return {
          qualityControl: rerunQualityControl,
          rerunAttempted: true,
          rerunError,
        };
      }
    } catch (error) {
      rerunError = error instanceof Error ? error.message : '重新品控失败';
    }

    const polledQualityControl = await pollAssetQualityControl(
      () => this.getAssetQualityControl(assetId),
      {
        baselineCheckedAt,
        untilNotStale: true,
        maxWaitMs: 180000,
        intervalMs: 3000,
      },
    );
    if (polledQualityControl) {
      return {
        qualityControl: polledQualityControl,
        rerunAttempted: true,
        rerunError,
      };
    }

    const data = await this.getAssetQualityControl(assetId).catch(() => null);
    return {
      qualityControl: data ? resolveAssetQualityControlPayload(data) : current || null,
      rerunAttempted: true,
      rerunError,
    };
  },

  async qualityControlReextract(assetId: string) {
    const payload = await apiClient.requestJson<{
      success?: boolean;
      data?: unknown;
      message?: string;
      error?: string;
    }>(`/api/assets/${encodeURIComponent(assetId)}/quality-control/re-extract`, {
      method: 'POST',
      body: {},
      auth: true,
      timeoutMs: appConfig.particleExtractionSubmitTimeoutMs,
    });

    if (payload?.success === false) {
      throw new Error(payload.error || '重新提取失败');
    }

    const data = payload?.data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const record = data as Record<string, unknown>;
      if (
        (record.status === 'processing' || record.status === 'queued')
        && typeof record.taskId === 'string'
      ) {
        return {
          accepted: true as const,
          taskId: record.taskId,
          status: record.status as string,
        };
      }
    }

    return { accepted: false as const, data };
  },
};
